/**
 * THE FIXTURE BACKEND — the other half of the seam, and the only one that runs today.
 *
 * M0 ships no feature and no operation is implemented, so a screen with nothing behind it cannot
 * be looked at, let alone reviewed. This module is a real in-memory implementation of
 * `ApplicationApi`: it holds state between calls, derives the completeness percentage the way the
 * server is specified to derive it, refuses with the registered machine codes, and moves through
 * the application display states in the same order the specification says the server does.
 *
 * IT IS NOT A STUB AND MUST NOT DRIFT INTO ONE. Everything it enforces is copied from a cited
 * place in the corpus, and a rule it gets wrong is a rule the UI will be built against wrongly.
 * When `http.ts` becomes the live implementation this file stays, because a deterministic backend
 * is what the screen tests run against.
 *
 * ALL TIMESTAMPS ARE FIXED STRINGS. Partly because C11 bans reading the clock outside the
 * injected Clock (eslint `no-restricted-syntax`), and partly because a fixture that changes with
 * the wall clock is a fixture whose screenshots never match twice.
 */
import type {
  Application,
  ApplicationDecision,
  ApplicationState,
  ApplicationSubmitRequest,
  AuthorSession,
  AuthorStatus,
  CategoryList,
  CompletenessItem,
  Credential,
  CredentialList,
  CredentialUploadClaim,
  OwnProfile,
  OwnProfileUpdate,
  SlugAvailability,
  StoredImage,
  TermsAcceptanceRequest,
  TermsState,
  UploadClaim,
} from '../contract';
import { ApiError } from './problem';
import type { ApplicationApi, CallOptions, QuarantineUploader } from './types';

// -------------------------------------------------------------------------------------------
// The seeded lookups, verbatim. These are DATA on the server and are read from it at run time —
// they are here only because the fixture is standing in for the server, and a UI that reads them
// from `Application.items` rather than from this file is the point (see domain/completeness.ts).
// -------------------------------------------------------------------------------------------

/**
 * `profile_completeness_item`, all nine rows in `sort_order`, with design 23's labels (04-schema.md
 * seeds the old ones): (field_code, weight, is_required_to_submit, sort_order, label_en). Weights
 * sum to 100 and exactly four rows are required to submit — a name, a face, a voice and a subject.
 */
const COMPLETENESS_SEED = [
  { fieldCode: 'display_name', weight: 15, isRequiredToSubmit: true, label: 'Author name' },
  { fieldCode: 'avatar', weight: 15, isRequiredToSubmit: true, label: 'A profile photo' },
  { fieldCode: 'bio_long', weight: 20, isRequiredToSubmit: true, label: 'Full bio' },
  {
    fieldCode: 'expertise',
    weight: 10,
    isRequiredToSubmit: true,
    label: 'At least one main area',
  },
  { fieldCode: 'headline', weight: 10, isRequiredToSubmit: false, label: 'Headline' },
  { fieldCode: 'bio_short', weight: 10, isRequiredToSubmit: false, label: 'Short bio' },
  { fieldCode: 'about', weight: 10, isRequiredToSubmit: false, label: 'Anything else' },
  { fieldCode: 'social_link', weight: 5, isRequiredToSubmit: false, label: 'A link to your work' },
  { fieldCode: 'credential', weight: 5, isRequiredToSubmit: false, label: 'Identity evidence' },
] as const;

/** `category`, all eight rows in `sort_order`. The camel case is deliberate and load-bearing. */
const CATEGORY_SEED: CategoryList = {
  items: [
    { code: 'moon', label: 'Moon', sortOrder: 1 },
    { code: 'astrology', label: 'Astrology', sortOrder: 2 },
    { code: 'astronomy', label: 'Astronomy', sortOrder: 3 },
    { code: 'healingHealth', label: 'Healing & Health', sortOrder: 4 },
    { code: 'coven', label: 'Coven', sortOrder: 5 },
    { code: 'tarot', label: 'Tarot', sortOrder: 6 },
    { code: 'personalJourney', label: 'Personal Journey', sortOrder: 7 },
    { code: 'holidays', label: 'Holidays', sortOrder: 8 },
  ],
};

/**
 * `reserved_name` seeds 16 patterns — impersonation and brand guards (moonx, admin, support,
 * staff, official …) and route collisions (api, authors, about …). The server is the authority;
 * this subset only makes the refusal reachable in the fixture.
 */
const RESERVED_SLUGS = new Set([
  'moonx',
  'moonx-team',
  'moonx-support',
  'admin',
  'support',
  'staff',
  'official',
  'api',
  'authors',
  'about',
]);

/** Addresses already spent. Current and retired share ONE namespace, so this list never shrinks. */
const TAKEN_SLUGS = new Set(['luna', 'selene', 'moon-child']);

const TERMS_VERSION = '2026-01';
const TERMS_EFFECTIVE_FROM = '2026-01-01T00:00:00Z';
const TERMS_CONTENT_URL = 'https://moonx.app/legal/creator-terms';
const T_SUBMITTED = '2026-08-18T09:14:00Z';
const T_DECIDED = '2026-08-21T16:02:00Z';
const T_FILED = '2026-08-18T09:11:00Z';
const T_ACCEPTED = '2026-08-18T09:10:00Z';

/** Which of the eight situations the fixture starts in. Every one of them is reachable in the UI. */
export type FixtureScenario =
  | 'empty'
  | 'partial'
  | 'ready'
  | 'in_review'
  | 'needs_more_info'
  | 'approved'
  | 'terms_reacceptance'
  | 'complete';

export interface FixtureOptions {
  readonly scenario?: FixtureScenario;
  /** Simulated round trip, so loading states are visible rather than theoretical. */
  readonly latencyMs?: number;
  /**
   * Make the next call of this kind fail, so the error states can be looked at. The key is the
   * method name; the value is the ApiError to throw. Consumed on use.
   */
  readonly failures?: Partial<Record<keyof ApplicationApi, ApiError>>;
}

const LONG_BIO_SAMPLE =
  'I have been reading the sky since I was small enough to be carried outside to see it. ' +
  'These days I write about what the moon is actually doing, in plain language, for people who ' +
  'want something steadier than a horoscope and warmer than an ephemeris. I trained as a nurse ' +
  'and I still think like one: what is the evidence, what is the person in front of me actually ' +
  'asking, and what would help tonight. My favourite pieces are the ones that send somebody ' +
  'outside to look up for the first time in years, and my least favourite are the ones that ' +
  'promise more than the sky can deliver. I am slow, I check things twice, and I would rather ' +
  'file a week late than file something I would not stand behind in a year. If you have ever ' +
  'wondered why the moon looked enormous on the horizon and ordinary an hour later, I have ' +
  'written about that, and I will happily write about it again.';

interface FixtureState {
  authorRowExists: boolean;
  authorStatus: AuthorStatus;
  profile: {
    displayName: string | null;
    slug: string | null;
    slugFrozen: boolean;
    avatarUrl: string | null;
    headline: string | null;
    bioLong: string | null;
    bioShort: string | null;
    about: string | null;
    experience: string | null;
    approach: string | null;
    education: string | null;
    pronouns: string | null;
    timezone: string | null;
    languageCode: string | null;
    contactFormEnabled: boolean;
    visibility: Record<string, boolean>;
    expertise: Array<{ categoryCode: string; isPublic: boolean }>;
    tags: Array<{ tagText: string; isPublic: boolean }>;
    links: Array<{ linkKind: string; url: string; isPublic: boolean; sortOrder?: number }>;
    privateEmail: string | null;
    availabilityNote: string | null;
    rateMinor: number | null;
    rateCurrency: string | null;
    rateUnit: string | null;
  };
  credentials: Credential[];
  acceptedTermsVersion: string | null;
  acceptedAt: string | null;
  /** Append-only, one row per round. `round` is its 1-based index plus one. */
  rounds: Array<{
    submittedAt: string;
    completenessAtSubmit: number;
    decision: ApplicationDecision | null;
  }>;
}

function emptyProfileState(): FixtureState['profile'] {
  return {
    displayName: null,
    slug: null,
    slugFrozen: false,
    avatarUrl: null,
    headline: null,
    bioLong: null,
    bioShort: null,
    about: null,
    experience: null,
    approach: null,
    education: null,
    pronouns: null,
    timezone: null,
    languageCode: null,
    contactFormEnabled: false,
    // Everything public by default EXCEPT the timezone — the one field whose default is private.
    visibility: {
      headline: true,
      bioLong: true,
      experience: true,
      approach: true,
      education: true,
      pronouns: true,
      timezone: false,
    },
    expertise: [],
    tags: [],
    links: [],
    privateEmail: null,
    availabilityNote: null,
    rateMinor: null,
    rateCurrency: null,
    rateUnit: null,
  };
}

function seedState(scenario: FixtureScenario): FixtureState {
  const state: FixtureState = {
    authorRowExists: false,
    authorStatus: 'applicant',
    profile: emptyProfileState(),
    credentials: [],
    acceptedTermsVersion: null,
    acceptedAt: null,
    rounds: [],
  };

  if (scenario === 'empty') return state;

  state.authorRowExists = true;
  state.profile.displayName = 'Mira Halloway';
  state.profile.headline = 'Nurse, night-sky writer, reluctant early riser';

  if (scenario === 'partial') return state;

  // Everything the four required rows want, plus a couple of the weighted optional ones.
  state.profile.avatarUrl = 'https://img.moonx.art/authors/mira/avatar-512.webp';
  state.profile.bioLong = LONG_BIO_SAMPLE;
  state.profile.bioShort = 'Writes about what the moon is actually doing, in plain language.';
  state.profile.slug = 'mira-halloway';
  state.profile.expertise = [
    { categoryCode: 'moon', isPublic: true },
    { categoryCode: 'healingHealth', isPublic: false },
  ];
  state.profile.tags = [{ tagText: 'night sky', isPublic: true }];
  state.profile.links = [
    { linkKind: 'website', url: 'https://mirahalloway.example', isPublic: true, sortOrder: 1 },
  ];
  state.acceptedTermsVersion = TERMS_VERSION;
  state.acceptedAt = T_ACCEPTED;

  if (scenario === 'ready') return state;

  if (scenario === 'terms_reacceptance') {
    // An approved author meeting the one-time cutover interstitial: their acceptance is of an
    // older label, so `blocksSubmission` is true and `termsAcceptanceRequired` is raised.
    state.authorStatus = 'approved';
    state.profile.slugFrozen = true;
    state.acceptedTermsVersion = '2025-06';
    state.rounds = [
      {
        submittedAt: T_SUBMITTED,
        completenessAtSubmit: 85,
        decision: { outcome: 'approved', decidedAt: T_DECIDED, messageToApplicant: null },
      },
    ];
    return state;
  }

  state.credentials = [
    { fileName: 'nursing-registration.pdf', filedAt: T_FILED, applicationRound: 1 },
  ];
  state.rounds = [{ submittedAt: T_SUBMITTED, completenessAtSubmit: 90, decision: null }];

  if (scenario === 'in_review') return state;

  if (scenario === 'needs_more_info') {
    const round = state.rounds[0];
    if (round) {
      round.decision = {
        outcome: 'needs_more_info',
        decidedAt: T_DECIDED,
        messageToApplicant:
          'Thank you — we enjoyed the bio. Before we can say yes we would like to see one piece ' +
          'you have already published, so please add a link to your work. A profile photo where we ' +
          'can see your face would help too; the current one is quite dark.',
      };
    }
    state.profile.links = [];
    return state;
  }

  // approved
  const round = state.rounds[0];
  if (round) {
    round.decision = { outcome: 'approved', decidedAt: T_DECIDED, messageToApplicant: null };
  }
  state.authorStatus = 'approved';
  state.profile.slugFrozen = true;
  return state;
}

// -------------------------------------------------------------------------------------------
// Derivation — the part that must match the server, because the UI is built against it.
// -------------------------------------------------------------------------------------------

function isFilled(value: string | null): boolean {
  return value !== null && value.trim().length > 0;
}

function completenessItems(state: FixtureState): CompletenessItem[] {
  const p = state.profile;
  const satisfied: Record<string, boolean> = {
    display_name: isFilled(p.displayName),
    avatar: isFilled(p.avatarUrl),
    // The band is the gate, not mere presence: bioLong is 600–1200 or null, with no in-between.
    bio_long: p.bioLong !== null && p.bioLong.length >= 600 && p.bioLong.length <= 1200,
    expertise: p.expertise.length > 0,
    headline: isFilled(p.headline),
    bio_short: isFilled(p.bioShort),
    about: isFilled(p.about),
    social_link: p.links.length > 0,
    credential: state.credentials.some((c) => c.fileName !== null),
  };

  return COMPLETENESS_SEED.map((row) => ({
    fieldCode: row.fieldCode,
    label: row.label,
    weight: row.weight,
    isRequiredToSubmit: row.isRequiredToSubmit,
    isSatisfied: satisfied[row.fieldCode] === true,
  }));
}

function completenessPercent(items: readonly CompletenessItem[]): number {
  return items.reduce((sum, item) => (item.isSatisfied ? sum + item.weight : sum), 0);
}

function termsAreCurrent(state: FixtureState): boolean {
  return state.acceptedTermsVersion === TERMS_VERSION;
}

function openRound(state: FixtureState): FixtureState['rounds'][number] | undefined {
  const last = state.rounds[state.rounds.length - 1];
  return last && last.decision === null ? last : undefined;
}

function lastDecision(state: FixtureState): ApplicationDecision | null {
  const last = state.rounds[state.rounds.length - 1];
  return last?.decision ?? null;
}

function deriveApplication(state: FixtureState): Application {
  const items = completenessItems(state);
  const percent = completenessPercent(items);
  const requiredMet = items.every((item) => !item.isRequiredToSubmit || item.isSatisfied);
  const last = state.rounds[state.rounds.length - 1];
  const decision = lastDecision(state);

  let displayState: ApplicationState;
  if (openRound(state) !== undefined) {
    displayState = 'in_review';
  } else if (decision?.outcome === 'approved') {
    displayState = 'approved';
  } else if (decision !== null) {
    // needs_more_info. `rejected` cannot be produced: `work_queue_decision` seeds exactly two
    // outcomes for kind `application` and there is no applicant -> disabled transition row.
    displayState = 'needs_more_info';
  } else {
    displayState = requiredMet && submissionSettled(state) ? 'ready' : 'empty';
  }

  // `canSubmit` is true when every REQUIRED item is satisfied and the live terms are accepted.
  // It is a convenience for the button; the server is the authority either way.
  const canSubmit =
    requiredMet &&
    submissionSettled(state) &&
    displayState !== 'in_review' &&
    displayState !== 'approved';

  return {
    state: displayState,
    round: state.rounds.length === 0 ? null : state.rounds.length,
    submittedAt: last?.submittedAt ?? null,
    completenessPercent: percent,
    completenessAtSubmit: last?.completenessAtSubmit ?? null,
    canSubmit,
    items,
    decision,
  };
}

function deriveSession(state: FixtureState): AuthorSession {
  const application = deriveApplication(state);
  return {
    authorId: state.authorRowExists ? 'aut_7Q2mK4xV' : null,
    displayName: state.profile.displayName,
    authorStatus: state.authorStatus,
    applicationState: application.state,
    // The demo always offers it; the server says true for no author row and for an applicant (design 25).
    canApply: true,
    canUseEditor: state.authorStatus === 'approved',
    // Any author row (design 25): an applicant reaches the cabinet too, its pages empty.
    cabinetReachable: state.authorRowExists,
    disabledReason: null,
    termsAcceptanceRequired: state.authorStatus === 'approved' && !submissionSettled(state),
    slug: state.profile.slug,
    slugFrozen: state.profile.slugFrozen,
  };
}

function deriveProfile(state: FixtureState): OwnProfile {
  const p = state.profile;
  return {
    authorId: state.authorRowExists ? 'aut_7Q2mK4xV' : null,
    displayName: p.displayName,
    slug: p.slug,
    slugFrozen: p.slugFrozen,
    avatarUrl: p.avatarUrl,
    headline: p.headline,
    bioLong: p.bioLong,
    bioShort: p.bioShort,
    about: p.about,
    experience: p.experience,
    approach: p.approach,
    education: p.education,
    pronouns: p.pronouns,
    timezone: p.timezone,
    languageCode: p.languageCode,
    contactFormEnabled: p.contactFormEnabled,
    visibility: {
      headline: p.visibility.headline ?? true,
      bioLong: p.visibility.bioLong ?? true,
      experience: p.visibility.experience ?? true,
      approach: p.visibility.approach ?? true,
      education: p.visibility.education ?? true,
      pronouns: p.visibility.pronouns ?? true,
      timezone: p.visibility.timezone ?? false,
    },
    expertise: p.expertise.map((e) => ({ categoryCode: e.categoryCode, isPublic: e.isPublic })),
    tags: p.tags.map((t) => ({ tagText: t.tagText, isPublic: t.isPublic })),
    links: p.links.map((l) => ({
      linkKind: l.linkKind,
      url: l.url,
      isPublic: l.isPublic,
      ...(l.sortOrder === undefined ? {} : { sortOrder: l.sortOrder }),
    })),
    signs: { birthDataOnFile: false, computedSignsPublic: false },
    private: {
      email: p.privateEmail,
      availabilityNote: p.availabilityNote,
      rateMinor: p.rateMinor,
      rateCurrency: p.rateCurrency,
      rateUnit: p.rateUnit,
    },
  };
}

function deriveTerms(state: FixtureState): TermsState {
  const accepted = termsAreCurrent(state);
  return {
    versionLabel: TERMS_VERSION,
    effectiveFrom: TERMS_EFFECTIVE_FROM,
    contentUrl: TERMS_CONTENT_URL,
    isMaterialChange: true,
    accepted,
    acceptedAt: accepted ? state.acceptedAt : null,
    // Blocks submit, resubmit and publish — and NEVER a payout.
    blocksSubmission: !accepted,
  };
}

const SLUG_SHAPE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

function problem(status: number, errorCode: string, title: string, detail: string): ApiError {
  return new ApiError(
    status,
    {
      type: `https://moonx.art/problems/${errorCode.toLowerCase().replace(/_/g, '-')}`,
      title,
      status,
      detail,
      error_code: errorCode as never,
    },
    title,
  );
}

// -------------------------------------------------------------------------------------------

export function createFixtureApplicationApi(options: FixtureOptions = {}): ApplicationApi {
  const state = withStandards(seedState(seededAs(options.scenario)), options);
  const latency = options.latencyMs ?? 220;
  const failures: Partial<Record<keyof ApplicationApi, ApiError>> = { ...options.failures };

  async function settle<T>(
    name: keyof ApplicationApi,
    call: CallOptions | undefined,
    produce: () => T,
  ): Promise<T> {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(resolve, latency);
      call?.signal?.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      });
    });
    const planned = failures[name];
    if (planned) {
      delete failures[name];
      throw planned;
    }
    return produce();
  }

  /**
   * THE UNRESOLVED QUESTION, MADE VISIBLE RATHER THAN HIDDEN. Nothing in the signed-off corpus
   * says which write creates the `author` row, and every table an applicant fills in has a NOT
   * NULL foreign key to it. The recommended reading is that `author_start` fires on the FIRST
   * write on the application path — whichever of acceptTerms or updateOwnProfile is reached
   * first — which is the only reading under which the four required items and the terms
   * acceptance can exist before submit. The fixture implements that reading so the flow can run;
   * if the owner rules differently, this function is the only place that changes.
   */
  function ensureAuthorRow(): void {
    state.authorRowExists = true;
  }

  return {
    getAuthorSession: (call) => settle('getAuthorSession', call, () => deriveSession(state)),

    getApplication: (call) => settle('getApplication', call, () => deriveApplication(state)),

    submitApplication: (body: ApplicationSubmitRequest, call) =>
      settle('submitApplication', call, () => {
        if (openRound(state) !== undefined || state.authorStatus === 'approved') {
          throw problem(
            409,
            'APPLICATION_ALREADY_OPEN',
            'There is already an open application.',
            'This application is already with the editorial team.',
          );
        }
        if (state.rounds.length > 0) {
          // A decided round exists: this is a resubmission and must use the other operation.
          throw problem(
            409,
            'APPLICATION_ALREADY_OPEN',
            'There is already an application on file.',
            'Use resubmit to open a new round.',
          );
        }
        if (body.acceptedTermsVersion !== state.acceptedTermsVersion || !submissionSettled(state)) {
          throw problem(
            422,
            'TERMS_ACCEPTANCE_STALE',
            'The author agreement has not been accepted.',
            'No acceptance of that version exists for this author.',
          );
        }
        const items = completenessItems(state);
        if (items.some((item) => item.isRequiredToSubmit && !item.isSatisfied)) {
          throw problem(
            422,
            'APPLICATION_INCOMPLETE',
            'A required item is missing.',
            'One of the four required items on the checklist is not yet satisfied.',
          );
        }
        state.rounds.push({
          submittedAt: T_SUBMITTED,
          completenessAtSubmit: completenessPercent(items),
          decision: null,
        });
        return deriveApplication(state);
      }),

    resubmitApplication: (body: ApplicationSubmitRequest, call) =>
      settle('resubmitApplication', call, () => {
        const decision = lastDecision(state);
        if (decision === null || decision.outcome === 'approved') {
          // NOTE: this 409 carries NO registered machine code — only the generic Problem. That is
          // the contract, not an omission here, so the UI branches on the status.
          throw new ApiError(
            409,
            undefined,
            'The last round has not been decided needs_more_info, so there is nothing to resubmit.',
          );
        }
        if (body.acceptedTermsVersion !== state.acceptedTermsVersion || !submissionSettled(state)) {
          throw problem(
            422,
            'TERMS_ACCEPTANCE_STALE',
            'The author agreement has not been accepted.',
            'No acceptance of that version exists for this author.',
          );
        }
        const items = completenessItems(state);
        if (items.some((item) => item.isRequiredToSubmit && !item.isSatisfied)) {
          throw problem(
            422,
            'APPLICATION_INCOMPLETE',
            'A required item is missing.',
            'One of the four required items on the checklist is not yet satisfied.',
          );
        }
        state.rounds.push({
          submittedAt: T_SUBMITTED,
          completenessAtSubmit: completenessPercent(items),
          decision: null,
        });
        return deriveApplication(state);
      }),

    getCurrentTerms: (call) => settle('getCurrentTerms', call, () => deriveTerms(state)),

    acceptTerms: (body: TermsAcceptanceRequest, call) =>
      settle('acceptTerms', call, () => {
        if (body.versionLabel !== TERMS_VERSION) {
          throw new ApiError(404, undefined, 'No such terms version, or it is superseded.');
        }
        if (state.acceptedTermsVersion === TERMS_VERSION) {
          throw new ApiError(409, undefined, 'This author has already accepted this version.');
        }
        ensureAuthorRow();
        state.acceptedTermsVersion = TERMS_VERSION;
        state.acceptedAt = T_ACCEPTED;
        return deriveTerms(state);
      }),

    getOwnProfile: (call) => settle('getOwnProfile', call, () => profileOf(state)),

    updateOwnProfile: (body: OwnProfileUpdate, call) =>
      settle('updateOwnProfile', call, () => {
        if (body.slug !== undefined && state.profile.slugFrozen) {
          throw problem(
            409,
            'SLUG_FROZEN',
            'The public page address is frozen.',
            'An approved author may not change their public page address.',
          );
        }
        if (typeof body.slug === 'string') {
          const slug = body.slug;
          if (!SLUG_SHAPE.test(slug) || slug.length < 3 || slug.length > 60) {
            throw problem(
              422,
              'PROFILE_LIMIT_EXCEEDED',
              'The address is outside the shape rule.',
              'Lowercase letters, digits and single hyphens, 3 to 60 characters.',
            );
          }
          if (RESERVED_SLUGS.has(slug)) {
            throw problem(
              422,
              'RESERVED_NAME',
              'That address is reserved.',
              'The address you asked for is reserved by the platform.',
            );
          }
          if (TAKEN_SLUGS.has(slug)) {
            throw problem(422, 'SLUG_TAKEN', 'That address is taken.', 'Choose another address.');
          }
        }
        if (
          typeof body.bioLong === 'string' &&
          (body.bioLong.length < 600 || body.bioLong.length > 1200)
        ) {
          throw problem(
            422,
            'PROFILE_LIMIT_EXCEEDED',
            'The bio is outside its band.',
            'The first-person bio is 600 to 1200 characters, or empty.',
          );
        }
        checkProfileFields(body);

        ensureAuthorRow();
        const p = state.profile;
        // Absent property = left alone; explicit null = clears. `undefined` means absent, which
        // is why every one of these is an `!== undefined` test and not a truthiness test.
        if (body.displayName !== undefined) p.displayName = body.displayName;
        if (body.slug !== undefined) p.slug = body.slug;
        if (body.headline !== undefined) p.headline = body.headline;
        if (body.bioLong !== undefined) p.bioLong = body.bioLong;
        if (body.bioShort !== undefined) p.bioShort = body.bioShort;
        if (body.about !== undefined) p.about = body.about;
        if (body.experience !== undefined) p.experience = body.experience;
        if (body.approach !== undefined) p.approach = body.approach;
        if (body.education !== undefined) p.education = body.education;
        if (body.pronouns !== undefined) p.pronouns = body.pronouns;
        if (body.timezone !== undefined) p.timezone = body.timezone;
        if (body.languageCode !== undefined) p.languageCode = body.languageCode;
        if (body.contactFormEnabled !== undefined) p.contactFormEnabled = body.contactFormEnabled;
        if (body.visibility !== undefined) {
          p.visibility = { ...p.visibility, ...body.visibility };
        }
        // WHOLE-SET REPLACEMENT for the three arrays — see domain/limits.ts on why, and the
        // finding that the contract does not say. Sending the array makes the stored set equal it.
        if (body.expertise !== undefined) {
          p.expertise = body.expertise.map((e) => ({
            categoryCode: e.categoryCode,
            isPublic: e.isPublic,
          }));
        }
        if (body.tags !== undefined) {
          p.tags = body.tags.map((t) => ({ tagText: t.tagText, isPublic: t.isPublic }));
        }
        if (body.links !== undefined) {
          p.links = body.links.map((l) => ({
            linkKind: l.linkKind,
            url: l.url,
            isPublic: l.isPublic,
            ...(l.sortOrder === undefined ? {} : { sortOrder: l.sortOrder }),
          }));
        }
        if (body.private !== undefined) {
          const priv = body.private;
          if (priv.email !== undefined) p.privateEmail = priv.email;
          if (priv.availabilityNote !== undefined) p.availabilityNote = priv.availabilityNote;
          if (priv.rateMinor !== undefined) p.rateMinor = priv.rateMinor;
          if (priv.rateCurrency !== undefined) p.rateCurrency = priv.rateCurrency;
          if (priv.rateUnit !== undefined) p.rateUnit = priv.rateUnit;
        }
        applyProfileFields(state, body);
        return profileOf(state);
      }),

    checkSlugAvailability: (slug: string | undefined, call) =>
      settle('checkSlugAvailability', call, (): SlugAvailability => {
        const suggestion = suggestSlug(state.profile.displayName);
        if (slug === undefined) {
          // No slug asked: this is the SUGGESTION path, and it is where transliteration lives.
          return {
            slug: null,
            available: false,
            reason: null,
            suggestion,
            frozen: state.profile.slugFrozen,
          };
        }
        if (state.profile.slugFrozen) {
          return {
            slug,
            available: false,
            reason: 'Your address is fixed.',
            suggestion: null,
            frozen: true,
          };
        }
        if (!SLUG_SHAPE.test(slug) || slug.length < 3 || slug.length > 60) {
          return {
            slug,
            available: false,
            reason: 'Outside the shape rule: lowercase letters, digits and single hyphens.',
            suggestion,
            frozen: false,
          };
        }
        if (RESERVED_SLUGS.has(slug)) {
          return {
            slug,
            available: false,
            reason: 'Reserved by MoonX.',
            suggestion,
            frozen: false,
          };
        }
        if (TAKEN_SLUGS.has(slug)) {
          return {
            slug,
            available: false,
            reason: 'Taken, or previously used and permanently reserved.',
            suggestion,
            frozen: false,
          };
        }
        return { slug, available: true, reason: null, suggestion: null, frozen: false };
      }),

    claimAvatarUpload: (body: UploadClaim, call) =>
      settle('claimAvatarUpload', call, (): StoredImage => {
        if (body.intakeId.length < 8 || body.intakeId.length > 64) {
          throw problem(
            404,
            'UPLOAD_OBJECT_NOT_FOUND',
            'No such object.',
            'There is no object with that intake id under your quarantine prefix.',
          );
        }
        ensureAuthorRow();
        // Replacing an avatar destroys BOTH previous copies in the same transaction. One author,
        // one avatar, always — which is why the UI confirms before replacing.
        state.profile.avatarUrl = `https://img.moonx.art/authors/${body.intakeId}/avatar-512.webp`;
        return {
          fileId: `fil_${body.intakeId.slice(0, 8)}`,
          url: state.profile.avatarUrl,
          widthPx: 512,
          heightPx: 512,
          byteSize: 48_120,
          contentType: 'image/webp',
        };
      }),

    listOwnCredentials: (call) =>
      settle('listOwnCredentials', call, (): CredentialList => ({ items: [...state.credentials] })),

    claimCredentialUpload: (body: CredentialUploadClaim, call) =>
      settle('claimCredentialUpload', call, (): Credential => {
        ensureAuthorRow();
        const record: Credential = {
          fileName: body.fileName,
          filedAt: T_FILED,
          applicationRound: state.rounds.length === 0 ? null : state.rounds.length,
        };
        state.credentials.push(record);
        return record;
      }),

    listEditorCategories: (call) => settle('listEditorCategories', call, () => CATEGORY_SEED),

    suggestTags: (query, call) => settle('suggestTags', call, () => tagSuggestionsFor(query?.q)),

    listAgreements: (call) => settle('listAgreements', call, () => agreementsOf(state)),

    acceptAgreements: (body, call) =>
      settle('acceptAgreements', call, () => {
        const known = agreementsOf(state).items;
        for (const item of body.accept) {
          const document = known.find((candidate) => candidate.documentCode === item.documentCode);
          if (document?.live?.versionLabel !== item.versionLabel) {
            throw new ApiError(404, undefined, 'No such document version, or it is superseded.');
          }
        }
        ensureAuthorRow();
        for (const item of body.accept) {
          // An item already accepted is a no-op, never a 409 (design 25, 3.1).
          if (item.documentCode === AUTHOR_AGREEMENT) {
            if (state.acceptedTermsVersion !== TERMS_VERSION) state.acceptedAt = T_ACCEPTED;
            state.acceptedTermsVersion = TERMS_VERSION;
          } else if (item.documentCode === EDITORIAL_STANDARDS && state.standards !== undefined) {
            state.standards.accepted = true;
          }
        }
        return agreementsOf(state);
      }),
  };
}

/**
 * The suggestion the server produces from the display name. The real one transliterates — an
 * author writing in Cyrillic or Greek needs a Latin candidate generated for them, and that is the
 * whole reason the parameter is optional. This fixture only lowercases and hyphenates, which is
 * enough to exercise the UI and is deliberately not passed off as the real transliteration.
 */
function suggestSlug(displayName: string | null): string | null {
  if (displayName === null) return null;
  const candidate = displayName
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return candidate.length >= 3 ? candidate.slice(0, 60) : null;
}

/**
 * The fixture's answer to leg one of an upload. It writes nowhere and reports progress so the
 * uploading state is visible; the real one is `firebase/storage`'s `uploadBytesResumable`.
 */
export function createFixtureQuarantineUploader(stepMs = 120): QuarantineUploader {
  return {
    putQuarantineObject: (_intakeId, _file, onProgress, call) =>
      new Promise<void>((resolve, reject) => {
        let done = 0;
        const timer = setInterval(() => {
          done += 0.25;
          onProgress(Math.min(done, 1));
          if (done >= 1) {
            clearInterval(timer);
            resolve();
          }
        }, stepMs);
        call?.signal?.addEventListener('abort', () => {
          clearInterval(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        });
      }),
  };
}

/* ======================================================= the tag list, written at the foot */

type TagAnswer = Awaited<ReturnType<ApplicationApi['suggestTags']>>;

/**
 * A SHORTENED COPY OF MOONX'S OWN TAG LIST (design 24, T6: `tag_catalogue_entry`, seeded from the
 * office sheet, docs/design/24-teammate-round-1/tags.tsv) - a few High-priority rows of five of its
 * groups, in the sheet's order, so the demo build's "Topics you know best" shows what the server
 * will answer. Written at the foot so the lines above, cited by number, keep theirs.
 */
const TAG_GROUPS: NonNullable<TagAnswer['groups']> = [
  { label: 'Moon', tags: ['Full moon', 'New moon', 'Lunar eclipse', 'Solar eclipse'] },
  { label: 'Zodiac', tags: ['Aries', 'Taurus', 'Gemini', 'Cancer'] },
  { label: 'Astrology', tags: ['Birth chart', 'Rising sign', 'Sun sign', 'Moon sign'] },
  { label: 'Tarot & divination', tags: ['Major arcana', 'Card meanings', 'Tarot spreads'] },
  { label: 'Practice', tags: ['Ritual', 'Meditation', 'Affirmations', 'Manifestation'] },
];

/** What other authors have used, lowercased as the server groups them, most used first. */
const OTHER_AUTHORS_TAGS: TagAnswer['items'] = [
  { tagText: 'moon phase', useCount: 41 },
  { tagText: 'night sky', useCount: 17 },
  { tagText: 'herbal tea', useCount: 6 },
];

/** As the server answers: MoonX's list only when nothing is typed; typed text narrows `items`. */
function tagSuggestionsFor(q: string | undefined): TagAnswer {
  const typed = (q ?? '').trim().toLowerCase();
  if (typed === '') return { items: OTHER_AUTHORS_TAGS, groups: TAG_GROUPS };
  return { items: OTHER_AUTHORS_TAGS.filter((tag) => tag.tagText.includes(typed)) };
}

/* ============================================ design 25's agreements, written at the foot */

/*
 * ★ THE EDITORIAL STANDARDS BESIDE THE AUTHOR AGREEMENT (design 25, 3.1; design 24, T24). The
 * standards are a second document an applicant accepts on the application's last step, and they
 * gate submitting as the agreement does (P12): so `submissionSettled`, not the agreement alone,
 * decides "ready", `canSubmit`, the session's `termsAcceptanceRequired` (an approved author who owes
 * one, 3.4) and the submit's TERMS_ACCEPTANCE_STALE. `editorialStandards: false` is a fresh
 * database, where only the author agreement has a version in force. A scenario that has already
 * submitted, or is ready to, has accepted them; the rest have accepted nothing.
 *
 * The state and the options are widened HERE, by declaration merging, so the lines above - cited by
 * number - keep theirs.
 */
interface FixtureState {
  /** The editorial standards: whether a version is in force, and whether it is accepted. */
  standards?: { readonly inForce: boolean; accepted: boolean };
  /** The profile's two design-25 fields (T25, T27), beside the profile declared above. */
  profileFields?: { yearsOfPractice: number | null; primaryLocation: PrimaryLocation | null };
}

export interface FixtureOptions {
  /** Whether the editorial standards have a version in force. Default true; false is a fresh box. */
  readonly editorialStandards?: boolean;
}

const AUTHOR_AGREEMENT = 'creator_program';
const EDITORIAL_STANDARDS = 'editorial_standards';
const STANDARDS_VERSION = '2026-10-11';
const STANDARDS_EFFECTIVE_FROM = '2026-10-11T00:00:00Z';
const STANDARDS_CONTENT_URL = 'https://moonx.app/legal/editorial-standards/2026-10-11';

const SCENARIOS_WITH_STANDARDS_ACCEPTED: ReadonlySet<FixtureScenario> = new Set([
  'ready',
  'in_review',
  'needs_more_info',
  'approved',
]);

/**
 * `complete` (design 25) is `ready` with nothing accepted yet: every required row filled, and the
 * application's last step, "Our standards and agreement", still to do - so the default build can
 * open it (`/apply?fixture=complete`) and a spec can reach it with no set-up.
 */
function seededAs(scenario: FixtureScenario | undefined): FixtureScenario {
  return scenario === 'complete' ? 'ready' : (scenario ?? 'empty');
}

function withStandards(state: FixtureState, options: FixtureOptions): FixtureState {
  state.standards = {
    inForce: options.editorialStandards ?? true,
    accepted: SCENARIOS_WITH_STANDARDS_ACCEPTED.has(options.scenario ?? 'empty'),
  };
  if (options.scenario === 'complete') {
    state.acceptedTermsVersion = null;
    state.acceptedAt = null;
  }
  state.profileFields = { yearsOfPractice: null, primaryLocation: null };
  return state;
}

/**
 * The profile as `getOwnProfile` answers it since design 25 (3.3): the members above, plus the
 * years of practice, the primary location and their two switches (public unless switched off).
 */
function profileOf(state: FixtureState): OwnProfile {
  const profile = deriveProfile(state);
  const fields = state.profileFields;
  const recorded = state.profile.visibility;
  return {
    ...profile,
    yearsOfPractice: fields?.yearsOfPractice ?? null,
    primaryLocation: fields?.primaryLocation ?? null,
    visibility: {
      ...profile.visibility,
      yearsOfPractice: recorded.yearsOfPractice ?? true,
      primaryLocation: recorded.primaryLocation ?? true,
    },
  };
}

/** The server's checks of the two fields (3.3): the pick is checked, never looked up. */
function checkProfileFields(body: OwnProfileUpdate): void {
  const years = body.yearsOfPractice;
  const place = body.primaryLocation;
  const yearsBad =
    typeof years === 'number' && (!Number.isInteger(years) || years < 0 || years > 80);
  const placeBad =
    place !== undefined &&
    place !== null &&
    (!Number.isInteger(place.placeId) ||
      place.placeId <= 0 ||
      place.label.trim() === '' ||
      place.label.length > 200 ||
      place.timeZone.trim() === '');
  if (yearsBad || placeBad) {
    throw new ApiError(
      422,
      {
        type: 'https://moonx.art/problems/profile-limit-exceeded',
        title: 'A value is outside its limit.',
        status: 422,
        error_code: 'PROFILE_LIMIT_EXCEEDED',
      },
      'A value is outside its limit.',
    );
  }
}

/** A pick sets the time zone to the place's; `null` clears the place and leaves the zone. */
function applyProfileFields(state: FixtureState, body: OwnProfileUpdate): void {
  const fields = state.profileFields ?? { yearsOfPractice: null, primaryLocation: null };
  if (body.yearsOfPractice !== undefined) fields.yearsOfPractice = body.yearsOfPractice;
  if (body.primaryLocation !== undefined) {
    fields.primaryLocation = body.primaryLocation;
    if (body.primaryLocation !== null) state.profile.timezone = body.primaryLocation.timeZone;
  }
  state.profileFields = fields;
}

/** Nothing a submit needs is owed: the live author agreement, and the standards where in force. */
function submissionSettled(state: FixtureState): boolean {
  const standards = state.standards;
  return termsAreCurrent(state) && (standards?.inForce !== true || standards.accepted);
}

/**
 * `listAgreements`, as the server answers it: the documents in their order (the standards' sort
 * order is 10, the agreement's 20). The titles are the cabinet's words; the cabinet names the
 * documents it knows by their code and never draws the server's title for them.
 */
function agreementsOf(state: FixtureState): AgreementList {
  const items: Agreement[] = [];
  const standards = state.standards;
  if (standards?.inForce === true) {
    items.push({
      documentCode: EDITORIAL_STANDARDS,
      title: 'Editorial standards',
      blocks: 'submission',
      live: {
        versionLabel: STANDARDS_VERSION,
        effectiveFrom: STANDARDS_EFFECTIVE_FROM,
        contentUrl: STANDARDS_CONTENT_URL,
        isMaterialChange: false,
      },
      accepted: standards.accepted
        ? {
            versionLabel: STANDARDS_VERSION,
            acceptedAt: T_ACCEPTED,
            contentUrl: STANDARDS_CONTENT_URL,
          }
        : null,
      owed: !standards.accepted,
      newerVersionWaiting: false,
    });
  }
  const accepted = state.acceptedTermsVersion;
  items.push({
    documentCode: AUTHOR_AGREEMENT,
    title: 'Author agreement',
    blocks: 'submission',
    live: {
      versionLabel: TERMS_VERSION,
      effectiveFrom: TERMS_EFFECTIVE_FROM,
      contentUrl: TERMS_CONTENT_URL,
      isMaterialChange: true,
    },
    accepted:
      accepted === null
        ? null
        : {
            versionLabel: accepted,
            acceptedAt: state.acceptedAt ?? T_ACCEPTED,
            contentUrl: TERMS_CONTENT_URL,
          },
    owed: !termsAreCurrent(state),
    newerVersionWaiting: accepted !== null && accepted !== TERMS_VERSION,
  });
  return { items };
}

import type { Agreement, AgreementList, PrimaryLocation } from '../contract';
